import React, { createContext, useContext, useState, useEffect } from 'react';

// Create the context
const ThemeContext = createContext();

// Theme configurations
export const themes = {
  dark: {
    name: 'dark',
    // Backgrounds
    bgPrimary: '#0a0a0f',
    bgSecondary: '#12121a',
    bgTertiary: '#1a1a24',
    bgCard: 'rgba(255, 255, 255, 0.02)',
    bgCardHover: 'rgba(255, 255, 255, 0.04)',
    bgInput: 'rgba(255, 255, 255, 0.05)',
    
    // Borders
    borderPrimary: 'rgba(255, 255, 255, 0.06)',
    borderSecondary: 'rgba(255, 255, 255, 0.1)',
    borderHover: 'rgba(255, 255, 255, 0.15)',
    
    // Text
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255, 255, 255, 0.85)',
    textMuted: 'rgba(255, 255, 255, 0.5)',
    textDisabled: 'rgba(255, 255, 255, 0.3)',
    
    // Accent colors (unchanged)
    accentPrimary: '#667eea',
    accentSecondary: '#764ba2',
    accentGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    
    // Status colors
    success: '#10b981',
    successBg: 'rgba(16, 185, 129, 0.1)',
    danger: '#ef4444',
    dangerBg: 'rgba(239, 68, 68, 0.1)',
    warning: '#f59e0b',
    warningBg: 'rgba(245, 158, 11, 0.1)',
    info: '#3b82f6',
    infoBg: 'rgba(59, 130, 246, 0.1)',
    
    // Shadows
    shadowSm: '0 2px 8px rgba(0, 0, 0, 0.3)',
    shadowMd: '0 8px 24px rgba(0, 0, 0, 0.4)',
    shadowLg: '0 16px 48px rgba(0, 0, 0, 0.5)',
    
    // Overlay
    overlay: 'rgba(0, 0, 0, 0.85)',
  },
  light: {
    name: 'light',
    // Backgrounds
    bgPrimary: '#f8fafc',
    bgSecondary: '#ffffff',
    bgTertiary: '#f1f5f9',
    bgCard: 'rgba(255, 255, 255, 0.8)',
    bgCardHover: 'rgba(255, 255, 255, 1)',
    bgInput: 'rgba(0, 0, 0, 0.03)',
    
    // Borders
    borderPrimary: 'rgba(0, 0, 0, 0.08)',
    borderSecondary: 'rgba(0, 0, 0, 0.12)',
    borderHover: 'rgba(0, 0, 0, 0.2)',
    
    // Text
    textPrimary: '#0f172a',
    textSecondary: '#334155',
    textMuted: '#64748b',
    textDisabled: '#94a3b8',
    
    // Accent colors (same)
    accentPrimary: '#667eea',
    accentSecondary: '#764ba2',
    accentGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    
    // Status colors
    success: '#059669',
    successBg: 'rgba(16, 185, 129, 0.1)',
    danger: '#dc2626',
    dangerBg: 'rgba(239, 68, 68, 0.1)',
    warning: '#d97706',
    warningBg: 'rgba(245, 158, 11, 0.1)',
    info: '#2563eb',
    infoBg: 'rgba(59, 130, 246, 0.1)',
    
    // Shadows
    shadowSm: '0 2px 8px rgba(0, 0, 0, 0.08)',
    shadowMd: '0 8px 24px rgba(0, 0, 0, 0.12)',
    shadowLg: '0 16px 48px rgba(0, 0, 0, 0.16)',
    
    // Overlay
    overlay: 'rgba(0, 0, 0, 0.5)',
  }
};

// Apply theme to CSS variables
const applyTheme = (theme) => {
  const root = document.documentElement;
  const themeValues = themes[theme];
  
  // Set CSS variables
  root.style.setProperty('--bg-primary', themeValues.bgPrimary);
  root.style.setProperty('--bg-secondary', themeValues.bgSecondary);
  root.style.setProperty('--bg-tertiary', themeValues.bgTertiary);
  root.style.setProperty('--bg-card', themeValues.bgCard);
  root.style.setProperty('--bg-card-hover', themeValues.bgCardHover);
  root.style.setProperty('--bg-input', themeValues.bgInput);
  
  root.style.setProperty('--border-primary', themeValues.borderPrimary);
  root.style.setProperty('--border-secondary', themeValues.borderSecondary);
  root.style.setProperty('--border-hover', themeValues.borderHover);
  
  root.style.setProperty('--text-primary', themeValues.textPrimary);
  root.style.setProperty('--text-secondary', themeValues.textSecondary);
  root.style.setProperty('--text-muted', themeValues.textMuted);
  root.style.setProperty('--text-disabled', themeValues.textDisabled);
  
  root.style.setProperty('--accent-primary', themeValues.accentPrimary);
  root.style.setProperty('--accent-secondary', themeValues.accentSecondary);
  root.style.setProperty('--accent-gradient', themeValues.accentGradient);
  
  root.style.setProperty('--success', themeValues.success);
  root.style.setProperty('--success-bg', themeValues.successBg);
  root.style.setProperty('--danger', themeValues.danger);
  root.style.setProperty('--danger-bg', themeValues.dangerBg);
  root.style.setProperty('--warning', themeValues.warning);
  root.style.setProperty('--warning-bg', themeValues.warningBg);
  root.style.setProperty('--info', themeValues.info);
  root.style.setProperty('--info-bg', themeValues.infoBg);
  
  root.style.setProperty('--shadow-sm', themeValues.shadowSm);
  root.style.setProperty('--shadow-md', themeValues.shadowMd);
  root.style.setProperty('--shadow-lg', themeValues.shadowLg);
  
  root.style.setProperty('--overlay', themeValues.overlay);
  
  // Set data attribute for CSS selectors
  root.setAttribute('data-theme', theme);
};

// Provider component
export function ThemeProvider({ children }) {
  // Initialize from localStorage or default to dark
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('elemental-theme');
    return saved || 'dark';
  });

  // Apply theme on mount and changes
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem('elemental-theme', theme);
  }, [theme]);

  // Toggle function
  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  // Set specific theme
  const setThemeMode = (mode) => {
    if (mode === 'dark' || mode === 'light') {
      setTheme(mode);
    }
  };

  const value = {
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    toggleTheme,
    setTheme: setThemeMode,
    colors: themes[theme]
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Custom hook
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;